import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ResponseModel } from '@dental-ui/account/models';
import { buildApiUrl, buildRequestHeaders } from '@dental-ui/shared/consts';

export interface MachineModelDto {
  id: string;
  name: string;
  machineTypeId: string;
  machineType: string;
  brandId: string;
  brand: string;
  fileUrl: string | null;
  isActive: boolean;
}

export interface SelectOption {
  id: string;
  name: string;
}

export interface PaginationData<T> {
  data: T[];
  totalPage: number;
  totalRecord: number;
}

export interface MachineModelListFilters {
  name?: string;
  brand?: string;
  machineType?: string;
}

@Injectable({ providedIn: 'root' })
export class PortalMachineModelsApiService {
  private readonly http = inject(HttpClient);

  getMachineModels(
    page: number,
    pageSize: number,
    filters: MachineModelListFilters = {}
  ): Observable<PaginationData<MachineModelDto>> {
    const body = {
      PaginationRequestModel: {
        page: page + 1,
        numberOfRecord: pageSize,
        sortColumn: 'Name',
        sortOrder: 'asc',
      },
      MachineModelFilterRequestDto: {
        name: filters.name ?? '',
        brand: filters.brand ?? '',
        machineType: filters.machineType ?? '',
      },
    };
    return this.http
      .post<ResponseModel<PaginationData<MachineModelDto>>>(
        buildApiUrl('machineModel.getAllByFilter'),
        body,
        { headers: buildRequestHeaders(true) }
      )
      .pipe(map((res) => res.data ?? { data: [], totalPage: 0, totalRecord: 0 }));
  }

  getAllBrands(): Observable<SelectOption[]> {
    return this.http
      .get<ResponseModel<PaginationData<SelectOption>>>(buildApiUrl('brand.getAll'), {
        headers: buildRequestHeaders(true),
      })
      .pipe(map((res) => res.data?.data ?? []));
  }

  getAllMachineTypes(): Observable<SelectOption[]> {
    return this.http
      .get<ResponseModel<SelectOption[]>>(buildApiUrl('machineType.getAll'), {
        headers: buildRequestHeaders(true),
      })
      .pipe(map((res) => res.data ?? []));
  }

  createMachineModel(model: Omit<MachineModelDto, 'id' | 'machineType' | 'brand' | 'fileUrl'>): Observable<unknown> {
    return this.http.post(buildApiUrl('machineModel.create'), model, {
      headers: buildRequestHeaders(true),
    });
  }

  updateMachineModel(model: MachineModelDto): Observable<unknown> {
    return this.http.put(buildApiUrl('machineModel.update'), model, {
      headers: buildRequestHeaders(true),
    });
  }

  deleteMachineModel(id: string): Observable<unknown> {
    return this.http.delete(`${buildApiUrl('machineModel.delete')}?machineModel=${id}`, {
      headers: buildRequestHeaders(true),
    });
  }
}
